import { useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Button, FileUploaderDropContainer, InlineNotification, Modal, Pagination, Select, SelectItem, TextInput } from '@carbon/react';
import { Upload } from '@carbon/icons-react';
import type { ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { DataTable } from '../components/DataTable';
import { InlineError, PageHeader } from '../components/states';
import { Money, When } from '../components/format';
import { BANK_TX_REASON, BANK_TX_SOURCE, BankTxStatus } from '../components/labels';
import { FilterBar, useUrlFilters, type FilterField } from '../components/Filters';

type Tx = {
  id: number; nguon: string; maGd: string; soTk: string | null; nganHang: string | null; amount: string; noiDung: string | null;
  maTim: string | null; lucGd: string | null; status: string; lyDo: string | null; orderId: string | null; nguoiXuLy: string | null;
  ghiChu: string | null; createdAt: string; xuLyLuc: string | null;
  order: { id: string; amount: string; trangThai: string; noiDung: string | null; gameUserId: number; username: string | null; nickname: string | null } | null;
};
type Page = { items: Tx[]; total: number; page: number; pageSize: number };
type Acc = { id: string; so: string; chuTk: string; bankName: string; state: string; bietDanh?: string };
type ImportResult = {
  summary: { rows: number; sent: number; skipped: number; matched: number; unmatched: number; duplicate: number; invalid: number };
  columns: Record<string, string>; skipped: { line: number; reason: string }[]; error: string | null;
};

const columns: ColumnDef<Tx, any>[] = [
  { header: 'Trạng thái', accessorKey: 'status', cell: (c) => <BankTxStatus s={c.getValue()} /> },
  { header: 'Lý do', accessorKey: 'lyDo', cell: (c) => (c.getValue() ? BANK_TX_REASON[c.getValue()] ?? c.getValue() : '—') },
  { header: 'Số tiền', accessorKey: 'amount', cell: (c) => <Money v={c.getValue()} /> },
  { header: 'Nội dung', accessorKey: 'noiDung', cell: (c) => <span className="cell-wrap">{c.getValue() || '—'}</span> },
  { header: 'Tài khoản nhận', id: 'tk', cell: (c) => [c.row.original.nganHang, c.row.original.soTk].filter(Boolean).join(' · ') || '—' },
  { header: 'Mã giao dịch', accessorKey: 'maGd', cell: (c) => c.getValue() || <span className="muted">(không mã)</span> },
  { header: 'Lệnh', id: 'lenh', cell: (c) => c.row.original.order
    ? <>{(c.row.original.order.username ?? c.row.original.order.nickname ?? '') + ' #' + c.row.original.order.gameUserId}</> : (c.row.original.maTim ?? '—') },
  { header: 'Nguồn', accessorKey: 'nguon', cell: (c) => BANK_TX_SOURCE[c.getValue()] ?? c.getValue() },
  { header: 'Nhận lúc', accessorKey: 'createdAt', cell: (c) => <When v={c.getValue()} /> },
];

const FIELDS: FilterField[] = [
  { key: 'status', label: 'Trạng thái', kind: 'select', options: [['', 'Tất cả'], ['chua_khop', 'Chưa khớp'], ['da_khop', 'Tự khớp'], ['da_ghep', 'Ghép tay'], ['bo_qua', 'Bỏ qua']] },
  { key: 'ref', label: 'Mã giao dịch / mã lệnh / nội dung / số TK', kind: 'text' },
  { key: 'lyDo', label: 'Lý do chưa khớp', kind: 'select', options: [['', 'Mọi lý do'], ...Object.entries(BANK_TX_REASON)] },
  { key: 'nguon', label: 'Nguồn', kind: 'select', options: [['', 'Mọi nguồn'], ...Object.entries(BANK_TX_SOURCE)] },
  { key: 'amountMin', label: 'Số tiền từ', kind: 'number' },
  { key: 'amountMax', label: 'Số tiền đến', kind: 'number' },
  { key: 'from', label: 'Từ ngày', kind: 'date' },
  { key: 'to', label: 'Đến ngày', kind: 'date' },
];
const KEYS = FIELDS.map((x) => x.key);

function ImportModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const accs = useQuery({ queryKey: ['wallet-accounts'], queryFn: () => api.get<{ items: Acc[] }>('/api/admin/wallet/accounts'), enabled: open });
  const [tk, setTk] = useState('');
  const [file, setFile] = useState<{ name: string; text: string } | null>(null);
  const [fileError, setFileError] = useState('');
  const run = useMutation({
    mutationFn: () => api.post<ImportResult>('/api/admin/wallet/bank-tx/import', { taiKhoanId: tk, fileName: file!.name, text: file!.text }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bank-tx'] }),
  });
  const close = () => { setFile(null); setTk(''); setFileError(''); run.reset(); onClose(); };
  const r = run.data;
  return (
    <Modal open={open} modalHeading="Nhập sao kê ngân hàng" primaryButtonText={r ? 'Xong' : 'Nhập'} secondaryButtonText="Đóng"
      primaryButtonDisabled={!r && (!tk || !file || run.isPending)} onRequestClose={close}
      onRequestSubmit={() => (r ? close() : run.mutate())}>
      <p className="muted">File CSV xuất từ ngân hàng hoặc SePay. Máy chủ tìm cột theo tên (mã giao dịch, số tiền vào, nội dung, thời gian), chỉ lấy tiền vào. Dòng có mã lệnh và đúng số tiền được cộng ngay; dòng khác vào hàng chưa khớp. Một mã giao dịch chỉ cộng một lần, nhập lại file không cộng thêm.</p>
      <Select id="imp-tk" labelText="Tài khoản nhận của sao kê" value={tk} onChange={(e) => setTk(e.target.value)} disabled={!!r}>
        <SelectItem value="" text="Chọn tài khoản" />
        {(accs.data?.items ?? []).map((a) => <SelectItem key={a.id} value={a.id} text={`${a.bankName} · ${a.so} · ${a.chuTk}${a.state === 'retired' ? ' (đã ngừng)' : ''}`} />)}
      </Select>
      {!r ? (
        <FileUploaderDropContainer labelText={file ? `Đã chọn: ${file.name}` : 'Kéo file CSV vào đây hoặc bấm để chọn'} accept={['.csv', '.txt', '.tsv', 'text/csv', 'text/plain']}
          onAddFiles={(_e, { addedFiles }) => {
            const f = addedFiles[0];
            setFileError('');
            if (!f) return;
            if (f.size > 3 * 1024 * 1024) { setFileError('File quá 3 MB'); return; }
            f.text().then((text) => setFile({ name: f.name, text }), () => setFileError('Không đọc được file'));
          }} />
      ) : null}
      {fileError ? <p className="text-error">{fileError}</p> : null}
      {r ? (
        <InlineNotification kind={r.error ? 'warning' : 'success'} lowContrast hideCloseButton
          title={`${r.summary.matched} dòng tự khớp, ${r.summary.unmatched} chưa khớp, ${r.summary.duplicate} đã có`}
          subtitle={`${r.summary.rows} dòng tiền vào, ${r.summary.skipped} dòng bỏ qua${r.summary.invalid ? `, ${r.summary.invalid} dòng lỗi` : ''}. Cột: ${Object.entries(r.columns).map(([k, v]) => `${k} = ${v}`).join(', ')}${r.error ? `. Dừng giữa chừng: ${r.error}` : ''}`} />
      ) : null}
      <InlineError error={run.error} />
    </Modal>
  );
}

export function BankTxPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const f = useUrlFilters(KEYS, { defaults: { status: 'chua_khop' } });
  const { page, pageSize } = f;
  const q = useQuery({
    queryKey: ['bank-tx', f.values, page, pageSize],
    queryFn: () => api.get<Page>('/api/admin/wallet/bank-tx', { ...f.query, page, pageSize }),
    placeholderData: keepPreviousData,
    refetchInterval: 30_000,
  });
  const [pick, setPick] = useState<Tx | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [orderId, setOrderId] = useState('');
  const [note, setNote] = useState('');
  const close = () => { setPick(null); setOrderId(''); setNote(''); match.reset(); dismiss.reset(); };
  const match = useMutation({
    mutationFn: () => api.post(`/api/admin/wallet/bank-tx/${pick!.id}/match`, { orderId: orderId.trim(), note: note.trim() }),
    onSuccess: () => { close(); qc.invalidateQueries({ queryKey: ['bank-tx'] }); qc.invalidateQueries({ queryKey: ['wallet'] }); },
  });
  const dismiss = useMutation({
    mutationFn: () => api.post(`/api/admin/wallet/bank-tx/${pick!.id}/dismiss`, { note: note.trim() }),
    onSuccess: () => { close(); qc.invalidateQueries({ queryKey: ['bank-tx'] }); },
  });
  const open = pick?.status === 'chua_khop' && can('wallet.match');
  return (
    <div className="page">
      <PageHeader title="Giao dịch ngân hàng" lead="Mọi khoản tiền vào tài khoản nhận: từ webhook SePay hoặc sao kê nhập tay. Khoản có mã lệnh và đúng số tiền đã được cộng tự động. Khoản chưa khớp (sai số tiền, thiếu mã, về sau khi lệnh hết hạn) chờ ở đây: ghép vào lệnh nạp của người chơi kèm lý do (cộng đúng số tiền đã về), hoặc bỏ qua nếu đã trả lại người gửi." />
      <FilterBar id="gd" fields={FIELDS} filters={f} exportPath="/api/admin/wallet/bank-tx">
        {can('wallet.match') ? <Button kind="tertiary" size="md" renderIcon={Upload} onClick={() => setImportOpen(true)}>Nhập sao kê</Button> : null}
        <Link to="/wallet/deposits" className="cds--btn cds--btn--ghost cds--btn--md">Lệnh nạp</Link>
      </FilterBar>
      <DataTable columns={columns} data={q.data?.items} loading={q.isLoading || q.isPlaceholderData} error={q.error}
        onRetry={() => q.refetch()} onRowClick={(r) => setPick(r)} rowKey={(r) => String(r.id)}
        empty={{ title: f.values.status === 'chua_khop' ? 'Không có giao dịch chưa khớp' : 'Không có giao dịch', body: 'Tiền vào từ webhook hoặc sao kê hiện ở đây.' }} />
      {q.data && q.data.total > 0 ? (
        <Pagination page={page} pageSize={pageSize} pageSizes={[20, 50, 100]} totalItems={q.data.total} onChange={({ page: p, pageSize: s }) => f.setPage(p, s)} />
      ) : null}
      <ImportModal open={importOpen} onClose={() => setImportOpen(false)} />
      <Modal open={!!pick} modalHeading={pick ? `Giao dịch ${pick.maGd}` : ''}
        primaryButtonText={open ? 'Ghép vào lệnh, cộng tiền' : 'Đóng'} secondaryButtonText={open ? 'Bỏ qua giao dịch' : undefined}
        primaryButtonDisabled={open && (!/^[0-9a-f]{16}$/.test(orderId.trim()) || note.trim().length < 3 || match.isPending)}
        onRequestClose={close}
        onRequestSubmit={() => (open ? match.mutate() : close())}
        onSecondarySubmit={() => { if (open && note.trim().length >= 3) dismiss.mutate(); }}>
        <p><Money v={pick?.amount} /> · <BankTxStatus s={pick?.status ?? ''} />{pick?.lyDo ? ' · ' + (BANK_TX_REASON[pick.lyDo] ?? pick.lyDo) : ''}</p>
        <p>Nội dung: {pick?.noiDung || '—'}</p>
        <p>Tài khoản nhận: {[pick?.nganHang, pick?.soTk].filter(Boolean).join(' · ') || '—'} · {BANK_TX_SOURCE[pick?.nguon ?? ''] ?? pick?.nguon}{pick?.lucGd ? ' · ' + pick.lucGd : ''}</p>
        {pick?.maTim ? <p>Mã lệnh trong nội dung: <Link to={'/wallet/deposits?status=&ref=' + encodeURIComponent(pick.maTim)}>{pick.maTim}</Link></p> : null}
        {pick?.order ? <p>Lệnh: <Link to={'/wallet/deposits?status=&ref=' + pick.order.id}>{pick.order.id}</Link> · {pick.order.username ?? pick.order.nickname} #{pick.order.gameUserId} · <Money v={pick.order.amount} /></p> : null}
        {pick?.ghiChu ? <p>Ghi chú: {pick.ghiChu}{pick.nguoiXuLy ? ` (${pick.nguoiXuLy})` : ''}</p> : null}
        {open ? (
          <>
            <TextInput id="gd-order" labelText="Mã lệnh nạp (16 ký tự, xem ở trang Nạp tiền)" value={orderId} onChange={(e) => setOrderId(e.target.value.trim().toLowerCase())} />
            <TextInput id="gd-note" labelText="Lý do (bắt buộc)" value={note} onChange={(e) => setNote(e.target.value)} />
            <p className="muted">Ghép cộng đúng số tiền của giao dịch này vào người chơi của lệnh. Lệnh đã được cộng thì không ghép được.</p>
          </>
        ) : pick?.status === 'chua_khop' ? <p className="muted">Tài khoản của bạn không có quyền ghép giao dịch.</p> : null}
        <InlineError error={match.error || dismiss.error} />
      </Modal>
    </div>
  );
}
